// Kill Sheet print path for the installed iOS home-screen app.
//
// window.print() opens the print sheet in a Safari tab, but an installed
// (standalone) web app has no browser chrome to host that sheet, so the call
// silently does nothing. In that one context we instead capture the existing
// on-screen print view (including the SVG schedule chart) as a multi-page
// Letter PDF and hand it to the iOS share sheet, which offers Print, Save to
// Files and AirDrop. Every other context keeps window.print().
//
// html2canvas and jsPDF are imported lazily so the normal print path and
// every other page pay nothing for them.

import { detectStandalone, detectIOS } from "@/hooks/usePwaInstall";

// Letter page with the same 12mm margins the @media print stylesheet uses.
const PAGE_W_IN = 8.5;
const PAGE_H_IN = 11;
const MARGIN_IN = 12 / 25.4;
const CSS_PX_PER_IN = 96;
const CONTENT_W_PX = Math.round((PAGE_W_IN - 2 * MARGIN_IN) * CSS_PX_PER_IN);
const CONTENT_H_PX = Math.floor((PAGE_H_IN - 2 * MARGIN_IN) * CSS_PX_PER_IN);
const SCALE = 2;

/** True only where window.print() is a no-op: the installed iOS app. */
export function needsPdfPrint() {
  return detectStandalone() && detectIOS();
}

// Pick page cut lines (CSS px from the article top) that land on a section
// or table-row boundary where possible, so rows are not sliced in half.
function _pageBreaks(totalH, boundaries) {
  const breaks = [];
  let start = 0;
  while (start < totalH) {
    const limit = start + CONTENT_H_PX;
    if (limit >= totalH) { breaks.push([start, totalH]); break; }
    const minEnd = start + CONTENT_H_PX * 0.3;
    let end = limit;
    for (const b of boundaries) {
      if (b > minEnd && b <= limit) end = b;
    }
    breaks.push([start, end]);
    start = end;
  }
  return breaks;
}

/** Render the print view element to a Letter PDF File. */
export async function buildKillSheetPdf(el, filename) {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import("html2canvas"), import("jspdf"),
  ]);

  let boundaries = [];
  const canvas = await html2canvas(el, {
    scale: SCALE,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: CONTENT_W_PX + 40,
    // Lay the clone out at printed width (not the phone's width) with the
    // print stylesheet's zero padding, then record where it can be cut.
    onclone: (doc, clone) => {
      clone.style.width = `${CONTENT_W_PX}px`;
      clone.style.minHeight = "0";
      clone.style.padding = "0";
      clone.style.margin = "0";
      const top = clone.getBoundingClientRect().top;
      const nodes = clone.querySelectorAll(":scope > *, tr");
      boundaries = Array.from(nodes, (n) => Math.round(n.getBoundingClientRect().top - top))
        .filter((b) => b > 0)
        .sort((a, b) => a - b);
    },
  });

  const totalH = canvas.height / SCALE;
  const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait" });
  const slice = document.createElement("canvas");
  slice.width = canvas.width;
  const ctx = slice.getContext("2d");

  _pageBreaks(totalH, boundaries).forEach(([start, end], i) => {
    const sy = Math.round(start * SCALE);
    const sh = Math.round(end * SCALE) - sy;
    slice.height = sh;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, sh);
    ctx.drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
    if (i > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG",
      MARGIN_IN, MARGIN_IN, CONTENT_W_PX / CSS_PX_PER_IN, sh / SCALE / CSS_PX_PER_IN);
  });

  return new File([pdf.output("blob")], filename, { type: "application/pdf" });
}

/**
 * Open the iOS share sheet with the PDF.
 * Returns "shared" | "cancelled" | "needs-gesture" | "opened".
 * "needs-gesture": iOS refused because the tap's user activation expired
 * while the PDF was being built — the caller should ask for one more tap.
 */
export async function sharePdfFile(file) {
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: file.name });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
      if (e && e.name === "NotAllowedError") return "needs-gesture";
      throw e;
    }
  }
  // No file sharing — hand the PDF over as a download, which iOS shows in
  // its viewer (with its own share/print button and a Done button). Never
  // navigate the app window itself: a standalone app has no Back button.
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  return "opened";
}
